"use client";

import { useEffect, useRef, useState, useMemo } from "react";
import { useRouter } from "next/navigation";
import { toast } from "react-toastify";
import { useAuth } from "@/context/AuthContext";
import { NAV_BY_KEY } from "@/components/ui/navItems";
import { toPath } from "@/features/landing/utils/router-helpers";
import { toA2 } from "@/features/landing/utils/country-name";



/** @typedef {"featured"|"update"|"account"} UpdateType */

/**
 * @typedef {Object} UpdateCard
 * @property {string} id
 * @property {UpdateType} type
 * @property {string} title
 * @property {string} body
 * @property {string=} badge
 * @property {string=} ctaLabel
 * @property {{to:string, auth?:boolean, disabled?:boolean, external?:boolean}=} target
 * @property {string|null=} image
 * @property {string=} offerHref     // featured CTA
 * @property {string=} brandHref     // featured card click
 */

/** @type {string} */
const STORAGE_KEY = "siteUpdates:hidden";

const UPDATES = [
  {
    title: "VISA Prepaid — New",
    body: "Use worldwide wherever Visa is accepted.",
    target: { to: "/vouchers/visa/us" },
    ctaLabel: "Buy Visa prepaid",
    badge: "New",
  },
];

/**
 * @param {{ featuredOffers?: FeaturedOffer[] }} props
 */
export default function SiteUpdatesCarouselClient({ featuredBrands = [] }) {

  const [open, setOpen] = useState(true);
  const [index, setIndex] = useState(0);

  const trackRef = useRef(null);
  const listboxId = "updates-carousel";
  const router = useRouter();
  const { isAuthenticated, isAdmin } = useAuth();

  useEffect(() => {
    // localStorage only on client
    setOpen(localStorage.getItem(STORAGE_KEY) !== "1");
  }, []);

const cards = useMemo(() => {
  const featured = (featuredBrands || [])
    .filter((x) => x && (x.brand || x.brandName))
    .slice(0, 8)
    .map((b) => {
      const brandLabel = b.brandName || b.brand || "Featured brand";
      const brandKey = b.brand; // <-- canonical provider key
      const a2 = toA2(b.country || "");
      const countrySeg = String(a2 || "").toLowerCase();

      return {
        id: `featured:${b.brand}`,
        type: "featured",
        title: brandLabel,
        body: b.description || "Browse all offers from this brand",
        badge: "Featured",
        ctaLabel: "View brand",

        // main click = brand page
        brandHref: countrySeg
          ? `/vouchers/${encodeURIComponent(brandKey)}/${encodeURIComponent(countrySeg)}`
          : `/vouchers/${encodeURIComponent(brandKey)}`,

        // optional secondary CTA
        offerHref: b.sampleOfferId
          ? `/vouchers/offer/${encodeURIComponent(b.sampleOfferId)}`
          : undefined,

        image: b.image || null,
      };
    });

  const updates = UPDATES.map((u, idx) => ({
    id: `update:${idx}:${u.title}`,
    type: "update",
    title: u.title,
    body: u.body,
    badge: u.badge || "Update",
    ctaLabel: u.ctaLabel || "Open",
    target: u.target ?? (u.navKey ? NAV_BY_KEY[u.navKey] : undefined),
    image: null,
  }));

  return [...featured, ...updates];
}, [featuredBrands]);

useEffect(() => {
  const el = trackRef.current;
  if (!el) return;

  let hovering = false;
  const onEnter = () => (hovering = true);
  const onLeave = () => (hovering = false);

  el.addEventListener("mouseenter", onEnter);
  el.addEventListener("mouseleave", onLeave);
  el.addEventListener("focusin", onEnter);
  el.addEventListener("focusout", onLeave);

  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let t;

  if (!prefersReduced && cards.length > 1) {
    t = window.setInterval(() => {
      if (!hovering) setIndex((i) => (i + 1) % cards.length);
    }, 4000);
  }

  return () => {
    if (t) clearInterval(t);
    el.removeEventListener("mouseenter", onEnter);
    el.removeEventListener("mouseleave", onLeave);
    el.removeEventListener("focusin", onEnter);
    el.removeEventListener("focusout", onLeave);
  };
}, [cards.length]);


  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const cards = el.querySelectorAll('[data-card="true"]');
    const active = cards[index];
    if (!active) return;

    const targetLeft = active.offsetLeft - (el.clientWidth - active.clientWidth) / 2;
    const maxLeft = el.scrollWidth - el.clientWidth;
    const left = Math.max(0, Math.min(targetLeft, maxLeft));
    el.scrollTo({ left, behavior: "smooth" });
  }, [index]);

if (!open || cards.length === 0) return null;

useEffect(() => {
  if (index >= cards.length) setIndex(0);
}, [cards.length, index]);

  const handleCTA = (target) => {
    if (!target) return;
    if (target.disabled) return toast.info("🚫 Not available for your plan yet.");
    if (target.auth && !isAuthenticated) {
      toast.info("🔐 Please log in first.");
      router.push("/login");
      return;
    }
    if (target.external) window.open(target.to, "_blank", "noopener,noreferrer");
    else router.push(toPath(target.to));
  };

  const handleAdminDismiss = () => {
    if (!isAdmin) return toast.info("Only admins can hide this.");
    setOpen(false);
    localStorage.setItem(STORAGE_KEY, "1");
  };
const go = (href) => {
  if (!href) return;
  router.push(href);
};

const onCardClick = (u) => {
  if (!u) return;

  if (u.type === "featured") {
    // Card click: brand page
    if (u.brandHref) return go(u.brandHref);
    return;
  }

  // update/account: use target
  if (u.target) return handleCTA(u.target);
};

return (
  <section
    aria-label="Featured"
    className={[
      "relative mb-4 rounded-2xl border border-border",
      "bg-surface/80 backdrop-blur-md",
      "shadow-glass shadow-glassInset",
      "text-textPrimary/90",
    ].join(" ")}
  >
    <header className="flex items-center gap-2 px-3 py-2 text-sm">
      <div className="px-3 pb-1 text-xs text-textSecondary">
        {cards[index]?.type === "featured" && "Promotions"}
        {cards[index]?.type === "update" && "Product updates"}
      </div>

      <div className="ml-auto flex items-center gap-2">
        <span aria-hidden className="relative hidden h-1 w-24 overflow-hidden rounded bg-border sm:block">
          <span className="absolute inset-y-0 left-0 w-1/2 bg-primary/70 motion-safe:animate-progress" />
        </span>

        {isAdmin && (
          <button
            onClick={handleAdminDismiss}
            title="Dismiss for this browser"
            aria-label="Dismiss"
            className="rounded px-2 text-lg leading-none text-textSecondary hover:text-textPrimary/90"
          >
            &times;
          </button>
        )}
      </div>
    </header>

    <div className="px-3 pb-3">
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex w-full items-center justify-between">
          <button
            type="button"
            className={[
              "pointer-events-auto ml-1 rounded-full p-2",
              "border border-border bg-surfaceElevated/60 backdrop-blur-lg",
              "text-textSecondary hover:text-textPrimary/90",
              "transition focus-visible:outline-none focus-visible:shadow-glowAccent",
            ].join(" ")}
            aria-controls={listboxId}
            aria-label="Previous"
            onClick={() => setIndex((i) => (i - 1 + cards.length) % cards.length)}
          >
            ‹
          </button>

          <button
            type="button"
            className={[
              "pointer-events-auto mr-1 rounded-full p-2",
              "border border-border bg-surfaceElevated/60 backdrop-blur-lg",
              "text-textSecondary hover:text-textPrimary/90",
              "transition focus-visible:outline-none focus-visible:shadow-glowAccent",
            ].join(" ")}
            aria-controls={listboxId}
            aria-label="Next"
            onClick={() => setIndex((i) => (i + 1) % cards.length)} // ✅ fixed
          >
            ›
          </button>
        </div>

        <div
          id={listboxId}
          ref={trackRef}
          role="listbox"
          aria-label="Featured list"
          className="flex gap-3 overflow-x-auto scroll-smooth snap-x snap-mandatory px-1 py-1 [&>*]:snap-center [scrollbar-width:none] overscroll-x-contain touch-pan-x"
          style={{
            scrollBehavior: "smooth",
            overscrollBehaviorX: "contain",
            touchAction: "pan-x",
            WebkitOverflowScrolling: "touch",
          }}
        >
      {cards.map((u, i) => {
  const isActive = i === index;
  const clickable = Boolean(u.brandHref || u.target);

  return (
    <div
      key={u.id ?? `${u.title ?? "card"}-${i}`}
      data-card="true"
      role="option"
      aria-selected={isActive}
      tabIndex={clickable ? 0 : -1}
      onClick={clickable ? () => onCardClick(u) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onCardClick(u);
              }
            }
          : undefined
      }
      className={[
        "min-w-[260px] max-w-xs rounded-2xl border border-border p-4",
        "bg-surfaceElevated/60 backdrop-blur-lg",
        "shadow-glass shadow-glassInset",
        "focus:outline-none",
        clickable
          ? [
              "cursor-pointer transition",
              "hover:-translate-y-[1px] hover:border-borderStrong hover:bg-surfaceElevated/80",
              "active:scale-[0.99]",
              "focus-visible:shadow-glowAccent",
            ].join(" ")
          : "focus-visible:shadow-glowAccent",
        isActive ? "ring-1 ring-borderStrong" : "",
      ].join(" ")}
    >
      {/* Header row: logo + title/badge */}
      <div className="mb-2 flex items-start gap-3">
        <div
          className={[
            "h-12 w-12 shrink-0 rounded-xl border border-border",
            "bg-white/95 dark:bg-white/90",
            "grid place-items-center overflow-hidden",
          ].join(" ")}
        >
          {u.image ? (
            <img
              src={u.image}
              alt={`${u.title ?? "Brand"} logo`}
              className="h-full w-full object-contain p-2"
              loading="lazy"
              draggable={false}
            />
          ) : (
            <div className="text-[10px] text-textSecondary">N/A</div>
          )}
        </div>

        <div className="min-w-0 flex-1 text-left">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-sm font-semibold text-textPrimary/90">
              {u.title || u.ctaLabel || "Update"}
            </h3>

            {u.badge && (
              <span className="shrink-0 rounded bg-primary/20 px-2 py-0.5 text-[10px] font-bold text-primary">
                {u.badge}
              </span>
            )}
          </div>

          <p className="mt-1 text-sm text-textSecondary line-clamp-3">
            {u.body}
          </p>
        </div>
      </div>

      {clickable && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (u.offerHref) return go(u.offerHref);
            return onCardClick(u);
          }}
          className={[
            "mt-2 inline-flex items-center gap-1 text-xs font-medium",
            "text-primary hover:text-primaryHover",
            "focus-visible:outline-none focus-visible:shadow-glowAccent rounded",
          ].join(" ")}
        >
          {u.ctaLabel || "Open"} <span aria-hidden>→</span>
        </button>
      )}
    </div>
  );
})}
        </div>

        <div className="mt-2 flex items-center gap-1.5 pl-3">
          {cards.map((_, i) => (
            <button
              key={`dot-${i}`}
              aria-label={`Go to slide ${i + 1}`}
              className={[
                "h-1.5 w-4 rounded-full transition",
                i === index ? "bg-primary" : "bg-border hover:bg-textSecondary",
                "focus-visible:outline-none focus-visible:shadow-glowAccent",
              ].join(" ")}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
      </div>

      <div className="mt-3 text-xs text-textSecondary">
        Questions? <strong className="text-textPrimary/90">support@reloadpi.com</strong> ·{" "}
        <a
          href="https://t.me/reloadpi"
          target="_blank"
          rel="noopener noreferrer"
          className="underline decoration-primary underline-offset-2 hover:text-textPrimary/90"
        >
          Telegram →
        </a>
      </div>
    </div>
  </section>
);
}
